const toNumber = (value) => {
	if (typeof value === "number") return Number.isFinite(value) ? value : 0;
	const parsed = Number.parseFloat(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const coerceDefaultValue = (defaultValue, field) => {
	if (defaultValue === null || defaultValue === undefined) return null;
	if (field?.dataType === "number" || field?.inputType === "number") {
		const parsed = Number.parseFloat(defaultValue);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return defaultValue;
};

const getComparableValue = (value) => {
	if (value === null || value === undefined) return null;
	if (typeof value === "object" && "id" in value) return value.id;
	return value;
};

const valuesMatch = (actual, expected) => {
	if (actual === expected) return true;
	if (actual != null && expected != null) return String(actual) === String(expected);
	return false;
};

const valueInList = (actual, list) =>
	Array.isArray(list) && list.some((candidate) => valuesMatch(actual, candidate));

// Used by the "exists"/"notExists" visibility operators. Unlike a plain
// null/undefined/"" check, this treats any falsy-ish value (0, false, "0")
// as "no value" too, since a field showing 0 shouldn't count as the user
// having provided a real answer for downstream visibility/calculations.
const isValueEmpty = (value) => {
	if (value === null || value === undefined) return true;
	if (typeof value === "string") return value.trim() === "" || value.trim() === "0";
	if (typeof value === "number") return value === 0 || Number.isNaN(value);
	if (typeof value === "boolean") return value === false;
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === "object") {
		if (Array.isArray(value.items)) return value.items.length === 0;
		if (Object.keys(value).length === 0) return true;
	}
	return false;
};

export const evaluateVisibilityRules = (visibilityRules, scopeValues) => {
	if (!visibilityRules) return true;

	const evaluateLeaf = (condition) => {
		const { field: refKey, operator, value: expected } = condition;
		const actual = scopeValues ? scopeValues[refKey] : undefined;
		const actualValue = getComparableValue(actual);
		switch (operator) {
			case "equals":
				return valuesMatch(actualValue, expected);
			case "notEquals":
				return !valuesMatch(actualValue, expected);
			case "in":
				return valueInList(actualValue, expected);
			case "notIn":
				return !valueInList(actualValue, expected);
			case "exists":
				return !isValueEmpty(actual);
			case "notExists":
				return isValueEmpty(actual);
			default:
				return true;
		}
	};

	// Legacy single-condition format
	if (visibilityRules.showWhen) {
		return evaluateLeaf(visibilityRules.showWhen);
	}

	// New grouped format: { operator: 'AND'|'OR', conditions: [...] }
	const operator = visibilityRules.operator || "AND";
	const conditions = visibilityRules.conditions || [];
	if (conditions.length === 0) return true;

	for (const condition of conditions) {
		const result = condition.conditions
			? evaluateVisibilityRules(condition, scopeValues)
			: evaluateLeaf(condition);

		if (operator === "OR" && result) return true;
		if (operator === "AND" && !result) return false;
	}

	return operator === "AND";
};

export const getDerivedPropertiesForSource = (fields = [], sourceFieldKey) => [
	...new Set(
		fields
			.filter(
				(field) =>
					field.name === "DerivedValue" &&
					(!field.derivedConfig?.mode || field.derivedConfig.mode === "entity") &&
					field.derivedConfig?.sourceField === sourceFieldKey &&
					field.derivedConfig?.sourceProperty
			)
			.map((field) => field.derivedConfig.sourceProperty)
	),
];

const computeValue = (field, values) => {
	const operands = field.computedConfig?.operands || [];
	if (operands.length === 0) return null;

	const numbers = operands.map((key) => toNumber(values[key]));
	switch (field.computedConfig?.operator) {
		case "add":
		case "sum":
			return numbers.reduce((total, value) => total + value, 0);
		case "subtract":
			return numbers.slice(1).reduce((total, value) => total - value, numbers[0]);
		case "multiply":
		case "product":
			return numbers.reduce((total, value) => total * value, 1);
		case "divide":
			return numbers.length >= 2 && numbers[1] !== 0
				? numbers[0] / numbers[1]
				: null;
		case "min":
			return Math.min(...numbers);
		case "max":
			return Math.max(...numbers);
		case "avg":
			return numbers.reduce((total, value) => total + value, 0) / numbers.length;
		default:
			return null;
	}
};

const applyDerivedCalculation = (baseValue, calculation, values) => {
	if (!calculation?.operator || !calculation?.field) return baseValue;

	const base = toNumber(baseValue);
	const operand = toNumber(values[calculation.field]);
	switch (calculation.operator) {
		case "add":
			return base + operand;
		case "subtract":
			return base - operand;
		case "multiply":
			return base * operand;
		case "divide":
			return operand === 0 ? null : base / operand;
		default:
			return baseValue;
	}
};

const resolveAggregateValue = (config, values) => {
	const items = values[config.groupField]?.items;
	if (!Array.isArray(items)) return null;
	const numbers = items.map((item) => toNumber(item?.[config.childField]));
	if (numbers.length === 0) return 0;

	switch (config.operator || "sum") {
		case "sum": return numbers.reduce((total, value) => total + value, 0);
		case "avg": return numbers.reduce((total, value) => total + value, 0) / numbers.length;
		case "min": return Math.min(...numbers);
		case "max": return Math.max(...numbers);
		default: return null;
	}
};

export const flattenFormFields = (fields = []) => {
	const result = [];
	for (const field of fields || []) {
		result.push(field);
		if (field.name === "Container" && Array.isArray(field.container)) {
			result.push(...flattenFormFields(field.container));
		}
	}
	return result;
};

export const resolveRuntimeValues = (fields = [], rawValues = {}) => {
	const values = { ...rawValues };

	// Visibility can depend on another DerivedValue/ComputedValue field (e.g. "show
	// this fee only if the group total exists"), so it cannot be evaluated once up
	// front against raw values only. Instead, re-check visibility for every field on
	// every pass, right before resolving it, so a field's dependency has a chance to
	// be resolved first (in this pass or an earlier one) before its own visibility
	// and value are (re)computed. Hidden fields have their value removed so later
	// fields/formulas never see stale or default data.
	const passes = Math.max(fields.length, 1);
	for (let pass = 0; pass < passes; pass += 1) {
		fields.forEach((field) => {
			if (!evaluateVisibilityRules(field?.visibilityRules, values)) {
				delete values[field.key];
				return;
			}
			if (field.name === "DerivedValue") {
				const config = field.derivedConfig || {};

				// If the field is rendered as a default-value dropdown and the user already
				// picked a value, keep that selection instead of recomputing it.
				if (
					config.defaultValueOptions &&
					values[field.key] !== undefined &&
					values[field.key] !== null
				) {
					return;
				}

				const mode = config.mode || "entity";
				if (mode === "formula") {
					values[field.key] =
						computeValue({ computedConfig: config }, values) ??
						coerceDefaultValue(config.defaultValue, field);
				} else if (mode === "aggregate") {
					values[field.key] =
						resolveAggregateValue(config, values) ??
						coerceDefaultValue(config.defaultValue, field);
				} else {
					const source = values[config.sourceField];
					const baseValue =
						source?.[config.sourceProperty] ??
						coerceDefaultValue(config.defaultValue, field);
					values[field.key] = applyDerivedCalculation(
						baseValue,
						config.calculation,
						values
					);
				}
			}

			if (field.name === "ComputedValue") {
				values[field.key] = computeValue(field, values);
			}
		});
	}

	return values;
};
